const INVISIBLE_CHARACTER_PATTERN = /[\u200B-\u200D\uFEFF]/g;
const SURROUNDING_WRAPPER_PATTERN =
  /^[\s"'`“”„‟‘’‚‛«»‹›]+|[\s"'`“”„‟‘’‚‛«»‹›]+$/g;
const TRAILING_URL_PUNCTUATION_PATTERN = /[),.;\]}>"'`“”„‟‘’‚‛«»‹›]+$/g;

function stripPasteWrappers(value: string): string {
  return value
    .replace(INVISIBLE_CHARACTER_PATTERN, "")
    .trim()
    .replace(SURROUNDING_WRAPPER_PATTERN, "")
    .trim();
}

function extractJsonStringField(
  value: string,
  fieldNames: ReadonlyArray<string>,
): string | undefined {
  try {
    const parsed: unknown = JSON.parse(value);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return undefined;
    }

    for (const [key, fieldValue] of Object.entries(parsed)) {
      if (fieldNames.includes(key) && typeof fieldValue === "string") {
        return fieldValue;
      }
    }
  } catch {
    return undefined;
  }

  return undefined;
}

function extractLabeledValue(
  value: string,
  labels: ReadonlyArray<string>,
): string | undefined {
  for (const label of labels) {
    const labelPattern = new RegExp(`^\\s*${label}\\s*[:=]\\s*(.+)$`, "i");
    const match = value.match(labelPattern);
    if (match?.[1]) {
      return match[1];
    }
  }

  return undefined;
}

/**
 * Normalize a user-entered Bridge URL before validation, testing, or persistence.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const bridgeUrl = normalizeBridgeUrlInput(' "https://example.test/_rest_" ');
 * ```
 */
export function normalizeBridgeUrlInput(value: string): string {
  const unwrapped = stripPasteWrappers(value);
  const extracted =
    extractJsonStringField(unwrapped, ["bridgeUrl", "insuiteRestUrl", "url"]) ??
    extractLabeledValue(unwrapped, ["Bridge URL", "Bridge-URL", "bridgeUrl"]) ??
    unwrapped;
  const urlMatch = extracted.match(/https?:\/\/[^\s"'`“”„‟‘’‚‛<>]+/i);
  const candidate = stripPasteWrappers(urlMatch?.[0] ?? extracted);
  return candidate.replace(TRAILING_URL_PUNCTUATION_PATTERN, "");
}

/**
 * Normalize a pasted SOLTOKEN before generating Doc-Cirrus auth headers.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const solToken = normalizeSolTokenInput(' "secret" ');
 * ```
 */
export function normalizeSolTokenInput(
  value: string | null | undefined,
): string | undefined {
  const unwrapped = stripPasteWrappers(value ?? "");
  const extracted =
    extractJsonStringField(unwrapped, ["solToken", "SOLTOKEN", "token"]) ??
    extractLabeledValue(unwrapped, ["SOLTOKEN", "solToken", "Token"]) ??
    unwrapped;
  const normalized = stripPasteWrappers(extracted);
  return normalized.length > 0 ? normalized : undefined;
}
